import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../../services/api';

interface Theme {
    theme_id: number;
    title: string;
    description: string;
    created_at: string;
    updated_at: string;
    is_active: boolean;
    question_papers_count: number;
}

interface ThemeFormData {
    title: string;
    description: string;
    is_active?: boolean;
}

const initialFormData: ThemeFormData = {
    title: '',
    description: '',
};

const ThemeManagement: React.FC = () => {
    const { t } = useTranslation();
    const [themes, setThemes] = useState<Theme[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<ThemeFormData>(initialFormData);
    const [error, setError] = useState<string | null>(null);
    const [editingThemeId, setEditingThemeId] = useState<number | null>(null);

    useEffect(() => {
        fetchThemes();
    }, []);

    const fetchThemes = async () => {
        try {
            const response = await axios.get('/quiz/themes');
            setThemes(response.data);
        } catch (error) {
            console.error('Failed to fetch themes:', error);
            setError(t('contentCreator.themeManagement.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        try {
            if (editingThemeId) {
                await axios.put(`/quiz/themes/${editingThemeId}`, formData);
            } else {
                await axios.post('/quiz/themes', formData);
            }
            setOpenDialog(false);
            setFormData(initialFormData);
            setEditingThemeId(null);
            fetchThemes();
        } catch (error: any) {
            setError(error.response?.data?.error || t(editingThemeId ? 'contentCreator.themeManagement.updateError' : 'contentCreator.themeManagement.createError'));
        }
    };

    const handleEdit = (theme: Theme) => {
        setFormData({
            title: theme.title,
            description: theme.description,
            is_active: theme.is_active,
        });
        setEditingThemeId(theme.theme_id);
        setOpenDialog(true);
    };

    const handleDelete = async (themeId: number) => {
        if (!window.confirm(t('contentCreator.themeManagement.deleteConfirm'))) return;
        
        try {
            await axios.delete(`/quiz/themes/${themeId}`);
            fetchThemes();
        } catch (error: any) {
            setError(error.response?.data?.error || t('contentCreator.themeManagement.deleteError'));
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData(initialFormData);
        setEditingThemeId(null);
        setError(null);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h4">{t('contentCreator.themeManagement.title')}</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenDialog(true)}
                            >
                                {t('contentCreator.themeManagement.createTheme')}
                            </Button>
                        </Box>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('contentCreator.themeManagement.table.title')}</TableCell>
                                        <TableCell>{t('contentCreator.themeManagement.table.description')}</TableCell>
                                        <TableCell align="center">{t('contentCreator.themeManagement.table.questionPapers')}</TableCell>
                                        <TableCell align="center">{t('contentCreator.themeManagement.table.status')}</TableCell>
                                        <TableCell align="center">{t('contentCreator.themeManagement.table.lastUpdated')}</TableCell>
                                        <TableCell align="center">{t('contentCreator.themeManagement.table.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {themes.map((theme) => (
                                        <TableRow key={theme.theme_id}>
                                            <TableCell>{theme.title}</TableCell>
                                            <TableCell>{theme.description}</TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={theme.question_papers_count}
                                                    color={theme.question_papers_count > 0 ? "primary" : "default"}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: '4px',
                                                        backgroundColor: theme.question_papers_count > 0 ? '#e3f2fd' : '#f5f5f5',
                                                        color: theme.question_papers_count > 0 ? '#1976d2' : '#757575',
                                                        fontWeight: 500,
                                                        '& .MuiChip-label': {
                                                            px: 1.5
                                                        }
                                                    }}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={theme.is_active ? t('contentCreator.themeManagement.table.active') : t('contentCreator.themeManagement.table.inactive')}
                                                    color={theme.is_active ? "success" : "error"}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: '4px',
                                                        backgroundColor: theme.is_active ? '#e6f4ea' : '#fde9e9',
                                                        color: theme.is_active ? '#1e8449' : '#d32f2f',
                                                        fontWeight: 500,
                                                        '& .MuiChip-label': {
                                                            px: 1.5
                                                        }
                                                    }}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {new Date(theme.updated_at).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary"
                                                    onClick={() => handleEdit(theme)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(theme.theme_id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>{editingThemeId ? t('contentCreator.themeManagement.editTheme') : t('contentCreator.themeManagement.createTheme')}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                name="title"
                                label={t('contentCreator.themeManagement.form.title')}
                                value={formData.title}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                name="description"
                                label={t('contentCreator.themeManagement.form.description')}
                                value={formData.description}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={4}
                                required
                                sx={{ mb: 2 }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{t('contentCreator.themeManagement.form.cancel')}</Button>
                        <Button type="submit" variant="contained" color="primary">
                            {editingThemeId ? t('contentCreator.themeManagement.form.update') : t('contentCreator.themeManagement.form.create')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Container>
    );
};

export default ThemeManagement;
